import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EmojiPicker from 'emoji-picker-react';
import { MentionsInput, Mention } from 'react-mentions';
import * as leoProfanity from 'leo-profanity';
import '../styles.css';
import { showToast } from '../../services';
import { chatActions } from '../../actions';
import { blockedwordActions } from '../../actions';
import { studentActions } from '../../actions';
import { gameActions } from '../../actions';
import { userActions } from '../../actions';

import defaultAvatar from '../../assets/avatar1.png';
import reportImg from '../../assets/Report.svg'

import { getSocket } from '../../services/socket';
import { useLoggedUser } from '../../hooks/useLoggedUser';
import { hasValidSession } from '../../helpers/authUtils';

// Memoized ChatMessage component to prevent re-renders of old messages
const ChatMessage = memo(({
  chat,
  isOwnMessage,
  isUserMentioned,
  senderImage,
  maskProfanity,
  loggedUserStudentProfileID
}) => {
  const chatKey = chat.ChatMessageID || `${chat.StudentProfileID}-${chat.CreatedTimestamp}`;
  const maskedChatMessage = maskProfanity(chat.ChatMessage);

  return (
    <div key={chatKey} className={isOwnMessage ? "msg right-msg" : "msg left-msg"}>
      <div className="msg-img">
        <img src={senderImage || defaultAvatar} alt="avatar" />
      </div>
      <div className={`msg-bubble ${isUserMentioned ? 'highlighted-bubble' : ''}`}>
        <div className="msg-info">
          <div className="msg-info-name">{isOwnMessage ? 'You' : chat.SenderName}</div>
          <div className="msg-info-time">{new Date(chat.CreatedTimestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
        </div>
        <div className="msg-text">{maskedChatMessage}</div>
      </div>
    </div>
  );
});

ChatMessage.displayName = 'ChatMessage';

const initialState = {
  chatMessage: '',
  classID: null,
  studentProfileID: null,
  isFlagged: 'N',
  flaggedWords: '',
  languageFlag: 'E',
  sendToProfileID: [],
  AdminUserID: null,
  submitted: false,
};

const BLOCKED_CHAT_PLACEHOLDER = 'You have been blocked from the Chat.';
const MESSAGE_MATCH_WINDOW_MS = 2 * 60 * 1000;

const normalizeMessageText = (messageText) => (
  typeof messageText === 'string' ? messageText.trim() : ''
);

const normalizeRecipientIds = (recipientIds) => (
  Array.isArray(recipientIds)
    ? recipientIds
      .map((recipientId) => recipientId?.toString?.() ?? '')
      .filter(Boolean)
      .sort()
    : []
);

const areSameRecipients = (leftRecipients, rightRecipients) => {
  const normalizedLeftRecipients = normalizeRecipientIds(leftRecipients);
  const normalizedRightRecipients = normalizeRecipientIds(rightRecipients);

  if (normalizedLeftRecipients.length !== normalizedRightRecipients.length) {
    return false;
  }

  return normalizedLeftRecipients.every((recipientId, index) => recipientId === normalizedRightRecipients[index]);
};

const areMatchingMessages = (pendingMessage, confirmedMessage) => {
  if (!pendingMessage || !confirmedMessage) return false;
  if (Number(pendingMessage.StudentProfileID) !== Number(confirmedMessage.StudentProfileID)) return false;
  if (normalizeMessageText(pendingMessage.ChatMessage) !== normalizeMessageText(confirmedMessage.ChatMessage)) return false;
  if (!areSameRecipients(pendingMessage.SendToProfileID, confirmedMessage.SendToProfileID)) return false;

  const pendingTimestamp = new Date(pendingMessage.CreatedTimestamp).getTime();
  const confirmedTimestamp = new Date(confirmedMessage.CreatedTimestamp).getTime();

  if (!Number.isFinite(pendingTimestamp) || !Number.isFinite(confirmedTimestamp)) {
    return true;
  }

  return Math.abs(confirmedTimestamp - pendingTimestamp) <= MESSAGE_MATCH_WINDOW_MS;
};

const removeMatchingPendingMessages = (pendingMessages, confirmedMessages) => {
  if (!Array.isArray(pendingMessages) || pendingMessages.length === 0) {
    return [];
  }

  const normalizedConfirmedMessages = Array.isArray(confirmedMessages)
    ? confirmedMessages
    : (confirmedMessages ? [confirmedMessages] : []);

  if (normalizedConfirmedMessages.length === 0) {
    return pendingMessages;
  }

  const remainingPendingMessages = [...pendingMessages];
  let removedAnyMessage = false;

  normalizedConfirmedMessages.forEach((confirmedMessage) => {
    const pendingMessageIndex = remainingPendingMessages.findIndex((pendingMessage) => (
      areMatchingMessages(pendingMessage, confirmedMessage)
    ));

    if (pendingMessageIndex !== -1) {
      remainingPendingMessages.splice(pendingMessageIndex, 1);
      removedAnyMessage = true;
    }
  });

  return removedAnyMessage ? remainingPendingMessages : pendingMessages;
};

const GroupChat = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState(initialState);
  const [errorMessage, setErrorMessage] = useState('');
  const chatContainerRef = useRef(null);

  const { loading, chatmessages, error } = useSelector((state) => state.chatmessages);
  const { blockedwords } = useSelector((state) => state.blockedwords);
  const { studentslist } = useSelector((state) => state.studentslist);
  const { blockedstatus } = useSelector((state) => state.games);
  const loggedUser = useLoggedUser();
  const loggedUserRef = useRef(loggedUser);
  loggedUserRef.current = loggedUser;

  const [originalMessage, setOriginalMessage] = useState('');
  const [pendingMessages, setPendingMessages] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [showReportPopup, setShowReportPopup] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportMentions, setReportMentions] = useState('');
  const [reportButton, setReportBtn] = useState('');
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);

  // Track socket connection so we can block send and show reconnecting state when truly disconnected
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const isBlocked = blockedstatus?.BlockChat === true;

  useEffect(() => {
    if (!loggedUser?.data || !hasValidSession()) return;
    dispatch(blockedwordActions.fetchBlockedWords());
    dispatch(chatActions.fetchChatMessages(loggedUser?.data?.ClassID));
    dispatch(studentActions.fetchStudentsList(loggedUser?.data?.ClassID));
    dispatch(gameActions.fetchBlockedStatus(loggedUser?.data?.StudentProfileID));
  }, [dispatch, loggedUser?.data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest('.emoji-picker-inside') && !event.target.closest('.emoji-btn')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) {
      setIsSocketConnected(false);
      return;
    }
    setIsSocketConnected(socket.connected);

    const handleConnect = () => {
      setIsSocketConnected(true);
      // No longer refetch messages on reconnect - only on initial load
      // This prevents reloading all messages when switching tabs
    };

    const handleDisconnect = () => {
      setIsSocketConnected(false);
    };

    const handleConnectError = () => {
      setIsSocketConnected(false);
    };

    const handleChatMessagePosted = (data) => {
      const messages = Array.isArray(data) ? data : (data ? [data] : []);
      if (messages.length === 0) return;

      const currentUser = loggedUserRef.current;
      const messageClassID = messages[0].ClassID;
      if (messageClassID !== currentUser?.data?.ClassID) return;

      const chatMessagesToAppend = messages.filter((message) => {
        if (message?.ClassID !== currentUser?.data?.ClassID) return false;
        return typeof message?.ChatMessage === 'string' && message.ChatMessage.trim() !== '';
      });

      if (chatMessagesToAppend.length > 0) {
        setPendingMessages((currentPendingMessages) => (
          removeMatchingPendingMessages(currentPendingMessages, chatMessagesToAppend)
        ));
        dispatch(chatActions.appendChatMessages(chatMessagesToAppend));
      }
    };

  socket.on('connect', handleConnect);
  socket.on('disconnect', handleDisconnect);
  socket.on('connect_error', handleConnectError);
  socket.on('chatMessagePosted', handleChatMessagePosted);

  return () => {
    socket.off('connect', handleConnect);
    socket.off('disconnect', handleDisconnect);
    socket.off('connect_error', handleConnectError);
    socket.off('chatMessagePosted', handleChatMessagePosted);
  };
}, [dispatch]);

useEffect(() => {
  setPendingMessages((currentPendingMessages) => (
    removeMatchingPendingMessages(currentPendingMessages, chatmessages)
  ));
}, [chatmessages]);

const displayMessages = useMemo(() => {
  const confirmedMessages = Array.isArray(chatmessages) ? chatmessages : [];

  if (pendingMessages.length === 0) {
    return confirmedMessages;
  }

  const visiblePendingMessages = removeMatchingPendingMessages(pendingMessages, confirmedMessages);
  return [...confirmedMessages, ...visiblePendingMessages];
}, [chatmessages, pendingMessages]);

useEffect(() => {
  if (chatContainerRef.current) {
  //   // Use requestAnimationFrame for smoother scroll animation
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    });
  }
}, [displayMessages]); // Scroll down whenever visible messages update

useEffect(() => {
  if (isBlocked) {
    setErrorMessage('');
    setShowEmojiPicker(false);
    setOriginalMessage('');
    setState((prevState) => ({
      ...prevState,
      chatMessage: '',
      submitted: false,
    }));
    return;
  }

  setErrorMessage('');
}, [isBlocked]);

useEffect(() => {
  dispatch(userActions.fetchLandingMedia())
    .then(response => {
      if (!Array.isArray(response)) return;
      response.forEach(media => {
        if (media.Category === "reportbutton") {
          setReportBtn(media.MediaURL);
        }
      });
    })
    .catch(error => {
      console.log('Error:: ', error);
    });
}, []);

// Pre-compile regex patterns for profanity masking
const badWordsRegexMap = useMemo(() => {
  const map = new Map();
  let badWords = leoProfanity.list();

  if (blockedwords && blockedwords.length > 0) {
    blockedwords.forEach((blockedWord) => {
      if (blockedWord.EnabledFlag === 'Y') {
        badWords.push(blockedWord.Word);
      }
    });
  }

  // Pre-compile all regex patterns once
  badWords.forEach((word) => {
    if (!word || !word.trim()) return;
    const safe = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    map.set(word, { regex: new RegExp(`\\b${safe}\\b`, 'gi'), mask: '*'.repeat(word.length) });
  });
  return map;
}, [blockedwords]);

// Memoized profanity masking function
const maskProfanity = useMemo(() => {
  return (text) => {
    if (typeof text !== 'string') return text;
    let result = text;
    for (const [word, { regex, mask }] of badWordsRegexMap) {
      result = result.replace(regex, mask);
    }
    return result;
  };
}, [badWordsRegexMap]);

const handleInputChange = (e) => {
  const inputText = e.target.value;
  setOriginalMessage(inputText);
  setErrorMessage('');

  // Check if any bad words are in the input using pre-compiled regex patterns
  let isFlagged = 'N';
  for (const [word, { regex }] of badWordsRegexMap) {
    regex.lastIndex = 0; // Reset regex state for global flag
    if (regex.test(inputText)) {
      isFlagged = 'Y';
      break; // No need to check further
    }
  }

  setState((prevState) => ({
    ...prevState,
    chatMessage: inputText,
    isFlagged: isFlagged,
  }));
};

const handleSendMessage = (e) => {
  e.preventDefault();

  // Check if user is blocked - don't show error message since placeholder already indicates this
  if (isBlocked) {
    return;
  }

  // Check if message is empty
  if (originalMessage.trim() === '') {
    setErrorMessage('Please type a message to continue.');
    return;
  }

  const socket = getSocket();
  if (!socket || !socket.connected) {
    setErrorMessage('Connection lost. Reconnecting… Please try again in a moment.');
    showToast('Chat is reconnecting. Please try again in a moment.', 'error');
    return;
  }

  setErrorMessage('');
  setState((prevState) => ({ ...prevState, submitted: true }));

  // Extract mentioned student IDs
  const mentionedStudents = [];
  const mentionPattern = /@(\w+)/g;
  let match;
  while ((match = mentionPattern.exec(originalMessage)) !== null) {
    const mentionedStudent = studentData.find(student => {
      return student.display.toLowerCase().includes(match[1].toLowerCase());
    });
    if (mentionedStudent) {
      mentionedStudents.push(mentionedStudent.id);
    }
  }

  const optimisticMessage = {
    ChatMessageID: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ChatMessage: originalMessage,
    ClassID: loggedUser?.data?.ClassID,
    StudentProfileID: loggedUser?.data?.StudentProfileID,
    SenderName: loggedUser?.data?.UserName,
    SenderProfileImage: senderProfileImageMap[loggedUser?.data?.StudentProfileID],
    CreatedTimestamp: new Date().toISOString(),
    SendToProfileID: mentionedStudents,
  };

  setPendingMessages((currentPendingMessages) => [...currentPendingMessages, optimisticMessage]);

  socket.emit('postChatMessage', {
    chatMessage: originalMessage,
    classID: loggedUser?.data?.ClassID,
    studentProfileID: loggedUser?.data?.StudentProfileID,
    isFlagged: state.isFlagged,
    flaggedWords: state.flaggedWords,
    languageFlag: state.languageFlag,
    sendToProfileID: mentionedStudents,
    AdminUserID: state.AdminUserID,
  });

  // Reset states after sending the message
  setOriginalMessage('');
  setState(initialState);
  setShowEmojiPicker(false);
};

const handleEmojiClick = (emojiData) => {
  const newMessage = originalMessage + emojiData.emoji;
  setOriginalMessage(newMessage);
  setState((prevState) => ({ ...prevState, chatMessage: newMessage }));
  setShowEmojiPicker(false);
};

const handleToggleReportPopup = () => {
  setShowReportPopup(!showReportPopup);
};

const handleReportMentionChange = (e) => {
  const inputText = e.target.value;
  setReportMentions(inputText);
};

const handleReportSubmit = () => {
  if (isReportSubmitting) return;

  // Validate if reportMentions and reportMessage are filled
  if (!reportMentions.trim() || !reportMessage.trim()) {
    showToast('Student Name and Report Message are required.', 'error');
    return;
  }

  const mentionedStudents = [];
  // Match @ followed by mention text (include emojis/Unicode); \w+ only matches [a-zA-Z0-9_]
  const mentionPattern = /@([^\s@]+)/g;
  let match;

  // Extract mentions from the reportMentions
  while ((match = mentionPattern.exec(reportMentions)) !== null) {
    const mentionText = match[1];
    const mentionedStudent = studentData.find(student => {
      const display = student.display ?? '';
      return display.includes(mentionText) || display.toLowerCase().includes(mentionText.toLowerCase());
    });

    if (mentionedStudent) {
      mentionedStudents.push(mentionedStudent.id);
    } else {
      // Show error if the mentioned student does not exist
      showToast(`Student @${match[1]} does not exist.`, 'error');
      return; // stop further execution if invalid mention
    }
  }

  // If no valid mentions were found, show error
  if (mentionedStudents.length === 0) {
    showToast('Please mention at least one valid student.', 'error');
    return;
  }

  const reportPayload = {
    ClassID: loggedUser?.data?.ClassID,
    StudentProfileID: loggedUser?.data?.StudentProfileID,
    ReportonStudentProfileID: mentionedStudents,
    Reason: reportMessage
  };

  setIsReportSubmitting(true);
  dispatch(chatActions.reportChatMessage(reportPayload))
    .then(response => {
      showToast('Your report has been filed.', 'success');
      setReportMessage('');
      setReportMentions('');
      setShowReportPopup(false);
    })
    .catch(error => {
      showToast(error?.message || error?.toString?.() || 'Report failed.', 'error');
    })
    .finally(() => {
      setIsReportSubmitting(false);
    });
};

const studentData = useMemo(() => (
  studentslist && studentslist.length > 0
    ? studentslist
      .filter((student) => student.StudentProfileID !== loggedUser?.data?.StudentProfileID)
      .map((student) => ({
        id: student.StudentProfileID.toString(),
        display: student.UserName
      }))
    : []
), [studentslist, loggedUser?.data?.StudentProfileID]);

// Use each sender's profile image from their most recent message so that when a user
// changes their avatar, all their messages (old and new) show the current image.
// Optimized to only recalculate when new messages are truly added
const senderProfileImageMap = useMemo(() => {
  const map = {};
  if (!displayMessages?.length) return map;
  // Iterate backwards (most recent first) to get latest image per sender
  for (let i = displayMessages.length - 1; i >= 0; i--) {
    const msg = displayMessages[i];
    const id = msg.StudentProfileID;
    if (id != null && map[id] === undefined && msg.SenderProfileImage) {
      map[id] = msg.SenderProfileImage;
    }
  }
  return map;
}, [displayMessages]);

return (
  <section className="msger chat-section">
    <header className="msger-header">
      <div className="msger-header-title">
        <i className="fas fa-comment-alt"></i> Game Chat
        {!isSocketConnected && (
          <span className="chat-reconnecting" title="Reconnecting…"> Reconnecting…</span>
        )}
      </div>
      <div className="report-btn-container">
        <button className="report-round-btn" title='Report' onClick={handleToggleReportPopup}>
          <img src={reportButton} alt="Report" />
        </button>
      </div>
    </header>

    <main className="msger-chat" ref={chatContainerRef}>
      {displayMessages?.map((chat, index) => {
        const isOwnMessage = chat.StudentProfileID === loggedUser?.data?.StudentProfileID;
        const isUserMentioned = chat.SendToProfileID?.includes(loggedUser?.data?.StudentProfileID?.toString());
        const senderImage = senderProfileImageMap[chat.StudentProfileID] || chat.SenderProfileImage;

        return (
          <ChatMessage
            key={chat.ChatMessageID || `${chat.StudentProfileID}-${chat.CreatedTimestamp}-${index}`}
            chat={chat}
            isOwnMessage={isOwnMessage}
            isUserMentioned={isUserMentioned}
            senderImage={senderImage}
            maskProfanity={maskProfanity}
            loggedUserStudentProfileID={loggedUser?.data?.StudentProfileID}
          />
        );
      })}
    </main>

    {errorMessage && <div className="error-message">{errorMessage}</div>}

    <form className="msger-inputarea" onSubmit={handleSendMessage}>
      <MentionsInput
        className={`msger-input ${isBlocked ? 'blocked-input' : ''}`}
        value={originalMessage}
        onChange={handleInputChange}
        placeholder={isBlocked ? BLOCKED_CHAT_PLACEHOLDER : "  Enter Message..."}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSendMessage(e);
          }
        }}
        disabled={isBlocked}
      >
        <Mention
          trigger="@"
          data={studentData}
          markup="@__display__"
          displayTransform={(id, display) => `@${display}`}
        />
      </MentionsInput>

      <button type="button" className="emoji-btn" onClick={() => !isBlocked && setShowEmojiPicker(!showEmojiPicker)} disabled={isBlocked}>
        <i className="fa fa-smile-o"></i>
      </button>

      {showEmojiPicker && (
        <div className="emoji-picker-inside">
          <EmojiPicker onEmojiClick={handleEmojiClick} skinTonesDisabled={true} previewConfig={{ showPreview: false }} searchDisabled={true} />
        </div>
      )}

      <button type="submit" className="msger-send-btn" disabled={isBlocked}><i className="fa fa-paper-plane"></i></button>
    </form>

    {showReportPopup && (
      <div className="report-popup">
        <div className="report-content">
          <MentionsInput
            value={reportMentions}
            onChange={handleReportMentionChange}
            className="report-input"
            placeholder="@Mention..."
            allowSpaceInQuery
            rows="6"
          >
            <Mention
              trigger="@"
              data={studentData}
              markup="@__display__"
              displayTransform={(id, display) => `@${display}`}
            />
          </MentionsInput>
          <textarea
            value={reportMessage}
            onChange={(e) => setReportMessage(e.target.value)}
            className="report-textarea"
            placeholder="Type your report..."
          />
          <div className="report-actions">
            <button
              type="button"
              onClick={handleReportSubmit}
              className="btn-sm report-btn"
              disabled={isReportSubmitting}
              aria-busy={isReportSubmitting}
            >
              {isReportSubmitting ? 'Submitting...' : 'Report'}
            </button>
            <button
              type="button"
              onClick={handleToggleReportPopup}
              className="btn-sm cancel-btn"
              disabled={isReportSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
  </section>
);
};

export default GroupChat;
